import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import Barcode from 'react-barcode';
import { Card, Button } from 'react-bootstrap';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './EtiquetaPreview.css';
import logoSistema from '../../logo.svg';
import { EtiquetaModelo } from '../../types/etiquetas';

export interface EtiquetaPreviewProps {
  dados: {
    codigo_material: string;
    descricao: string;
    quantidade: number;
    unidade: string;
    lote: string;
    numero_pallet: number | string;
    codigo_tetra?: string;
    codigo_fornecedor?: string;
    data?: string;
    tamanho_etiqueta: string;
    code_mode: string;
  };
  modelo?: EtiquetaModelo | null;
}

export interface EtiquetaPreviewHandle {
  generatePreview: () => Promise<void>;
}

type LayoutElement = {
  x_inicio: number;
  y_inicio: number;
  x_fim: number;
  y_fim: number;
  ativo?: boolean;
  fonte_tamanho_base?: number;
  fonte_negrito?: boolean;
  centralizado?: boolean;
  max_linhas?: number;
  espacamento_linha_percentual?: number;
  valores?: {
    x_offset_percentual?: number;
    fonte_tamanho_base?: number;
    fonte_negrito?: boolean;
  };
  tipo?: string;
  mostrar_label?: boolean;
  mostrar_hri?: boolean;
  label_texto?: string;
  label_y_offset?: number;
  rotacao_graus?: number;
};

type LayoutConfig = Record<string, LayoutElement>;

const isEnabled = (value: unknown, defaultValue = true) => {
  if (value === undefined || value === null) {
    return defaultValue;
  }
  if (value === false || value === 0 || value === '0' || value === 'false') {
    return false;
  }
  return true;
};

const DEFAULT_LAYOUT_CONFIG: LayoutConfig = {
  logo: {
    x_inicio: 1,
    y_inicio: 1,
    x_fim: 24,
    y_fim: 24,
    ativo: true,
  },
  codigo_material: {
    x_inicio: 8,
    y_inicio: 1,
    x_fim: 97,
    y_fim: 22,
    fonte_tamanho_base: 48,
    fonte_negrito: true,
    centralizado: true,
    ativo: true,
  },
  descricao_produto: {
    x_inicio: 2,
    y_inicio: 24,
    x_fim: 72,
    y_fim: 38,
    fonte_tamanho_base: 16,
    max_linhas: 2,
    ativo: true,
  },
  quantidade_info: {
    x_inicio: 2,
    y_inicio: 37,
    x_fim: 90,
    y_fim: 40,
    fonte_tamanho_base: 12,
    ativo: true,
    valores: {
      x_offset_percentual: 18,
      fonte_tamanho_base: 9,
      fonte_negrito: false,
    },
  },
  batch_info: {
    x_inicio: 2,
    y_inicio: 39,
    x_fim: 90,
    y_fim: 55,
    fonte_tamanho_base: 12,
    ativo: true,
    valores: {
      x_offset_percentual: 18,
      fonte_tamanho_base: 9,
      fonte_negrito: false,
    },
  },
  pallet_info: {
    x_inicio: 2,
    y_inicio: 50,
    x_fim: 90,
    y_fim: 60,
    fonte_tamanho_base: 12,
    ativo: true,
    valores: {
      x_offset_percentual: 18,
      fonte_tamanho_base: 9,
      fonte_negrito: false,
    },
  },
  barcode_vertical: {
    x_inicio: 70,
    y_inicio: 10,
    x_fim: 105,
    y_fim: 65,
    tipo: 'code128',
    mostrar_label: true,
    mostrar_hri: true,
    label_texto: 'Unit identifier',
    ativo: true,
  },
  barcode_horizontal: {
    x_inicio: 3,
    y_inicio: 64,
    x_fim: 97,
    y_fim: 100,
    tipo: 'code128',
    mostrar_label: true,
    mostrar_hri: false,
    label_texto: 'Quant data',
    label_y_offset: -1,
    ativo: true,
  },
};

const splitQuantBlocks = (cfg: LayoutElement) => {
  const yStart = Math.min(cfg.y_inicio, cfg.y_fim);
  const yEnd = Math.max(cfg.y_inicio, cfg.y_fim);
  const lineHeight = (yEnd - yStart) / 3 || 0;
  const base = {
    ...cfg,
    valores: {
      ...(cfg.valores || {}),
    },
  };
  return {
    quantidade_info: {
      ...base,
      y_inicio: yStart,
      y_fim: yStart + lineHeight,
    },
    batch_info: {
      ...base,
      y_inicio: yStart + lineHeight,
      y_fim: yStart + lineHeight * 2,
    },
    pallet_info: {
      ...base,
      y_inicio: yStart + lineHeight * 2,
      y_fim: yEnd,
    },
  };
};

const normalizeLayoutConfig = (raw?: Record<string, any> | null): LayoutConfig => {
  const rawElements = raw && typeof raw === 'object' && 'elementos' in raw ? raw.elementos : raw;
  const elements = rawElements && typeof rawElements === 'object' ? rawElements : {};
  const merged: LayoutConfig = {};
  const withSplit = { ...elements };
  if (
    elements.dados_quantitativos &&
    !elements.quantidade_info &&
    !elements.batch_info &&
    !elements.pallet_info
  ) {
    Object.assign(withSplit, splitQuantBlocks(elements.dados_quantitativos));
  }

  Object.keys(DEFAULT_LAYOUT_CONFIG).forEach((key) => {
    const base = DEFAULT_LAYOUT_CONFIG[key];
    const override = withSplit[key] || {};
    merged[key] = {
      ...base,
      ...override,
      valores: {
        ...(base.valores || {}),
        ...((override && override.valores) || {}),
      },
    };
  });

  Object.keys(withSplit).forEach((key) => {
    if (!merged[key]) {
      merged[key] = withSplit[key];
    }
  });

  return merged;
};

const parseTamanhoEtiqueta = (value?: string) => {
  if (!value) {
    return { larguraMm: 100, alturaMm: 50 };
  }

  const match = value.match(/(\d+(?:[.,]\d+)?)\s*[xX]\s*(\d+(?:[.,]\d+)?)/);
  if (!match) {
    return { larguraMm: 100, alturaMm: 50 };
  }

  const larguraMm = Number(match[1].replace(',', '.'));
  const alturaMm = Number(match[2].replace(',', '.'));
  return {
    larguraMm: Number.isFinite(larguraMm) ? larguraMm : 100,
    alturaMm: Number.isFinite(alturaMm) ? alturaMm : 50,
  };
};

const resolveSize = (modelo: EtiquetaModelo | null | undefined, tamanhoEtiqueta: string) => {
  if (modelo && Number.isFinite(modelo.largura_mm) && Number.isFinite(modelo.altura_mm)) {
    return {
      larguraMm: modelo.largura_mm,
      alturaMm: modelo.altura_mm,
    };
  }
  return parseTamanhoEtiqueta(tamanhoEtiqueta);
};

const calcFontSize = (base: number, scale: number) => {
  const raw = Math.round(base * scale);
  return Math.max(6, Math.min(32, raw));
};

const padLeft = (value: string, size: number) => value.padStart(size, '0');

const getUnitCode = (dados: EtiquetaPreviewProps['dados'], numeroPallet?: number) => {
  const lote = String(dados.lote || '');
  const palletValue = numeroPallet ?? Number(dados.numero_pallet || 0);
  const pallet = padLeft(String(palletValue), 4);
  return `${lote}${pallet}`;
};

const getQuantCode = (dados: EtiquetaPreviewProps['dados'], numeroPallet?: number) => {
  const lote = padLeft(String(dados.lote || ''), 10);
  const palletValue = numeroPallet ?? Number(dados.numero_pallet || 0);
  const pallet = padLeft(String(palletValue), 4);
  const codMat = String(dados.codigo_material || '').slice(0, -1) || '';
  const unidade = dados.unidade || 'PC';
  const qtd = padLeft(String(dados.quantidade || 0), 6);
  return `${lote}${pallet}${codMat}${unidade}${qtd}`;
};

const waitForImages = async (container: HTMLElement) => {
  const images = Array.from(container.querySelectorAll('img'));
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        }),
    ),
  );
};

export const EtiquetaPreview = forwardRef<EtiquetaPreviewHandle, EtiquetaPreviewProps>(
  ({ dados, modelo }, ref) => {
  const labelRef = useRef<HTMLDivElement | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const pdfZoom = 150;
  const parsedQuantidade = Number(dados.quantidade);
  const quantidadeEtiquetas = Math.max(1, Math.floor(parsedQuantidade > 0 ? parsedQuantidade : 1));
  const numeroPalletInicial = Number(dados.numero_pallet) || 1;

  const layout = useMemo(() => normalizeLayoutConfig(modelo?.layout_config ?? null), [modelo?.layout_config]);
  const size = useMemo(() => resolveSize(modelo ?? null, dados.tamanho_etiqueta), [modelo, dados.tamanho_etiqueta]);

  const pxPerMm = 3.7795275591;
  const larguraMm = Math.max(1, size.larguraMm);
  const alturaMm = Math.max(1, size.alturaMm);
  const margemBaseMm = 0;
  const margemEsquerdaMm = Math.max(0, modelo?.margem_esquerda_mm ?? margemBaseMm);
  const margemSuperiorMm = Math.max(0, modelo?.margem_superior_mm ?? margemBaseMm);
  const margemDireitaMm = Math.max(0, modelo?.margem_direita_mm ?? margemBaseMm);
  const margemInferiorMm = Math.max(0, modelo?.margem_inferior_mm ?? margemBaseMm);
  const escalaGeral = Math.min(larguraMm / 100, alturaMm / 50);

  const widthPx = Math.max(1, Math.round(larguraMm * pxPerMm));
  const heightPx = Math.max(1, Math.round(alturaMm * pxPerMm));
  const marginLeftPx = Math.max(0, Math.round(margemEsquerdaMm * pxPerMm));
  const marginTopPx = Math.max(0, Math.round(margemSuperiorMm * pxPerMm));
  const marginRightPx = Math.max(0, Math.round(margemDireitaMm * pxPerMm));
  const marginBottomPx = Math.max(0, Math.round(margemInferiorMm * pxPerMm));
  const innerWidth = Math.max(1, widthPx - marginLeftPx - marginRightPx);
  const innerHeight = Math.max(1, heightPx - marginTopPx - marginBottomPx);

  const toPxX = useCallback(
    (percent: number) => marginLeftPx + (innerWidth * percent) / 100,
    [innerWidth, marginLeftPx],
  );
  const toPxY = useCallback(
    (percent: number) => marginTopPx + (innerHeight * percent) / 100,
    [innerHeight, marginTopPx],
  );

  const getBox = useCallback(
    (cfg: LayoutElement) => {
      const xStart = toPxX(cfg.x_inicio);
      const xEnd = Math.max(toPxX(cfg.x_fim), xStart);
      const yStart = toPxY(cfg.y_inicio);
      const yEnd = Math.max(toPxY(cfg.y_fim), yStart);
      const left = xStart;
      const top = yStart;
      const width = Math.max(1, xEnd - xStart);
      const height = Math.max(1, yEnd - yStart);
      return {
        left,
        top,
        width,
        height,
      };
    },
    [toPxX, toPxY],
  );

  const getRotationStyle = (cfg?: LayoutElement): React.CSSProperties | undefined => {
    const rotation = cfg?.rotacao_graus ?? 0;
    if (!rotation) {
      return undefined;
    }
    return {
      transform: `rotate(${rotation}deg)`,
      transformOrigin: 'center',
    };
  };

  const logoEmpresa = `${process.env.PUBLIC_URL || ''}/logo_lw.png`;
  const logoDisplay = logoEmpresa || logoSistema;
  const [logoSrc, setLogoSrc] = useState<string>(logoDisplay);
  const logoWidthMm = modelo?.logo_largura_mm ?? 0;
  const logoHeightMm = modelo?.logo_altura_mm ?? 0;
  const logoPaddingMm = modelo?.logo_padding_mm ?? 0;
  const logoBox = useMemo(() => {
    if (!layout.logo) {
      return null;
    }
    const base = getBox(layout.logo);
    const paddingPx = Math.max(0, Math.round(logoPaddingMm * pxPerMm));
    return {
      left: base.left,
      top: base.top,
      width: base.width,
      height: base.height,
      padding: paddingPx,
    };
  }, [getBox, layout.logo, logoHeightMm, logoPaddingMm, logoWidthMm, pxPerMm]);

  useEffect(() => {
    let active = true;
    const loadLogo = async () => {
      if (!logoDisplay) {
        return;
      }
      try {
        const response = await fetch(logoDisplay, { cache: 'force-cache' });
        if (!response.ok) {
          throw new Error('logo fetch failed');
        }
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('image/svg')) {
          const text = await response.text();
          const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(text)}`;
          if (active) {
            setLogoSrc(dataUrl);
          }
          return;
        }
        const blob = await response.blob();
        const dataUrl = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(String(reader.result || ''));
          reader.readAsDataURL(blob);
        });
        if (active && dataUrl) {
          setLogoSrc(dataUrl);
        }
      } catch {
        if (active) {
          setLogoSrc(logoDisplay);
        }
      }
    };
    loadLogo();
    return () => {
      active = false;
    };
  }, [logoDisplay]);

  const logoImageStyle = useMemo<React.CSSProperties>(() => {
    const widthPx = logoWidthMm > 0 ? Math.round(logoWidthMm * pxPerMm) : null;
    const heightPx = logoHeightMm > 0 ? Math.round(logoHeightMm * pxPerMm) : null;
    return {
      width: widthPx ? `${widthPx}px` : '100%',
      height: heightPx ? `${heightPx}px` : '100%',
      maxWidth: '100%',
      maxHeight: '100%',
    };
  }, [logoHeightMm, logoWidthMm, pxPerMm]);

  const createPdfPreview = useCallback(
    async (download?: boolean) => {
      if (!labelRef.current) {
        return;
      }

      const elements = Array.from(
        labelRef.current.querySelectorAll<HTMLElement>('.etiqueta-layout'),
      );
      if (!elements.length) {
        return;
      }

      const canvasScale = 4;
      const orientation = larguraMm >= alturaMm ? 'landscape' : 'portrait';
      const pdf = new jsPDF({
        orientation,
        unit: 'mm',
        format: [larguraMm, alturaMm],
      });

      for (let i = 0; i < elements.length; i += 1) {
        const element = elements[i];
        await waitForImages(element);

        const canvas = await html2canvas(element, {
          backgroundColor: '#ffffff',
          scale: canvasScale,
          useCORS: true,
        });

        if (logoSrc && layout.logo && layout.logo.ativo !== false) {
          const target = logoBox ?? getBox(layout.logo);
          const paddingPx = logoBox?.padding ?? 0;
          const drawX = (target.left + paddingPx) * canvasScale;
          const drawY = (target.top + paddingPx) * canvasScale;
          const drawW = Math.max(1, (target.width - paddingPx * 2) * canvasScale);
          const drawH = Math.max(1, (target.height - paddingPx * 2) * canvasScale);

          await new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => {
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                const srcW = img.width || drawW;
                const srcH = img.height || drawH;
                const ratio = Math.min(drawW / srcW, drawH / srcH);
                const targetW = Math.max(1, srcW * ratio);
                const targetH = Math.max(1, srcH * ratio);
                const offsetX = drawX + (drawW - targetW) / 2;
                const offsetY = drawY + (drawH - targetH) / 2;
                ctx.drawImage(img, offsetX, offsetY, targetW, targetH);
              }
              resolve();
            };
            img.onerror = () => resolve();
            img.crossOrigin = 'anonymous';
            img.src = logoSrc;
          });
        }

        const imgData = canvas.toDataURL('image/png');
        if (i > 0) {
          pdf.addPage([larguraMm, alturaMm], orientation);
        }
        pdf.addImage(imgData, 'PNG', 0, 0, larguraMm, alturaMm);
      }

      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      setPdfUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        return url;
      });

      if (download) {
        const anchor = document.createElement('a');
        anchor.href = url;
        const palletFim = numeroPalletInicial + quantidadeEtiquetas - 1;
        anchor.download = `etiquetas_${numeroPalletInicial}-${palletFim}_${larguraMm}x${alturaMm}.pdf`;
        anchor.click();
      }
    },
    [
      alturaMm,
      larguraMm,
      layout.logo,
      logoBox,
      logoSrc,
      quantidadeEtiquetas,
      numeroPalletInicial,
      getBox,
    ],
  );

  useImperativeHandle(
    ref,
    () => ({
      generatePreview: () => createPdfPreview(),
    }),
    [createPdfPreview],
  );

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const renderCodigoMaterial = () => {
    const cfg = layout.codigo_material;
    if (!cfg || !isEnabled(cfg.ativo, true)) {
      return null;
    }
    const box = getBox(cfg);
    const fontSize = calcFontSize(cfg.fonte_tamanho_base || 20, escalaGeral);
    const justify = cfg.centralizado === false ? 'flex-start' : 'center';
    return (
      <div
        className="etiqueta-element etiqueta-text"
        style={{
          left: box.left,
          top: box.top,
          width: box.width,
          height: box.height,
          fontSize,
          fontWeight: cfg.fonte_negrito ? 700 : 500,
          justifyContent: justify,
          alignItems: 'center',
          ...getRotationStyle(cfg),
        }}
      >
        {dados.codigo_material || '---'}
      </div>
    );
  };

  const renderDescricao = () => {
    const cfg = layout.descricao_produto;
    if (!cfg || !isEnabled(cfg.ativo, true)) {
      return null;
    }
    const box = getBox(cfg);
    const fontSize = calcFontSize(cfg.fonte_tamanho_base || 10, escalaGeral);
    return (
      <div
        className="etiqueta-element etiqueta-text etiqueta-descricao"
        style={{
          left: box.left,
          top: box.top,
          width: box.width,
          height: box.height,
          fontSize,
          WebkitLineClamp: cfg.max_linhas || 2,
          ...getRotationStyle(cfg),
        }}
      >
        {dados.descricao || 'Produto'}
      </div>
    );
  };

  const renderInfoLine = (cfg: LayoutElement | undefined, label: string, value: string) => {
    if (!cfg || !isEnabled(cfg.ativo, true)) {
      return null;
    }
    const box = getBox(cfg);
    const labelFont = calcFontSize(cfg.fonte_tamanho_base || 8, escalaGeral);
    const valoresCfg = cfg.valores || {};
    const valueOffset = (box.width * (valoresCfg.x_offset_percentual || 15)) / 100;
    const valueFont = calcFontSize(valoresCfg.fonte_tamanho_base || 8, escalaGeral);

    return (
      <div
        className="etiqueta-element etiqueta-text"
        style={{
          left: box.left,
          top: box.top,
          width: box.width,
          height: box.height,
          fontSize: labelFont,
          alignItems: 'center',
          ...getRotationStyle(cfg),
        }}
      >
        <div className="etiqueta-quant-line">
          <span className="etiqueta-quant-label" style={{ width: valueOffset }}>
            {label}
          </span>
          <span
            className="etiqueta-quant-value"
            style={{ fontSize: valueFont, fontWeight: valoresCfg.fonte_negrito ? 700 : 400 }}
          >
            {value}
          </span>
        </div>
      </div>
    );
  };

  const renderBarcodeVertical = (numeroPallet?: number) => {
    const cfg = layout.barcode_vertical;
    if (!cfg || !isEnabled(cfg.ativo, true)) {
      return null;
    }
    const box = getBox(cfg);
    const labelFont = Math.max(8, Math.round(10 * escalaGeral));
    const labelY = Math.max(0, box.top - labelFont - 4);

    return (
      <>
        {isEnabled(cfg.mostrar_label, true) && (
          <div
            className="etiqueta-element etiqueta-label"
            style={{
              left: box.left,
              top: labelY,
              width: box.width,
              height: labelFont + 4,
              fontSize: labelFont,
              ...getRotationStyle(cfg),
            }}
          >
            {cfg.label_texto || 'Unit identifier'}
          </div>
        )}
        <div
          className="etiqueta-element etiqueta-barcode-vertical"
          style={{
            left: box.left,
            top: box.top,
            width: box.width,
            height: box.height,
            ...getRotationStyle(cfg),
          }}
        >
          <div className="etiqueta-barcode-rotate">
            <Barcode
              value={getUnitCode(dados, numeroPallet)}
              width={1}
              height={30}
              displayValue={isEnabled(cfg.mostrar_hri, true)}
              fontSize={labelFont}
              textMargin={0}
              margin={0}
              renderer="svg"
            />
          </div>
        </div>
      </>
    );
  };

  const renderBarcodeHorizontal = (numeroPallet?: number) => {
    const cfg = layout.barcode_horizontal;
    if (!cfg || !isEnabled(cfg.ativo, true)) {
      return null;
    }
    const box = getBox(cfg);
    const labelFont = Math.max(8, Math.round(10 * escalaGeral));
    const labelOffset = (cfg.label_y_offset ?? -1) * 12 * escalaGeral;
    const labelTop = box.top + labelOffset;

    return (
      <>
        {isEnabled(cfg.mostrar_label, true) && (
          <div
            className="etiqueta-element etiqueta-label etiqueta-label-left"
            style={{
              left: box.left,
              top: labelTop,
              width: box.width,
              height: labelFont + 4,
              fontSize: labelFont,
              ...getRotationStyle(cfg),
            }}
          >
            {cfg.label_texto || 'Quant data'}
          </div>
        )}
        <div
          className="etiqueta-element etiqueta-barcode-horizontal"
          style={{
            left: box.left,
            top: box.top,
            width: box.width,
            height: box.height,
            ...getRotationStyle(cfg),
          }}
        >
          <Barcode
            value={getQuantCode(dados, numeroPallet)}
            width={1}
            height={30}
            displayValue={isEnabled(cfg.mostrar_hri, false)}
            fontSize={labelFont}
            margin={0}
            renderer="svg"
          />
        </div>
      </>
    );
  };

  const renderSingleEtiqueta = (numeroPallet: number, index: number) => (
    <div
      key={`etiqueta-${index}`}
      className="etiqueta-layout"
      style={{
        width: `${widthPx}px`,
        height: `${heightPx}px`,
        marginBottom: quantidadeEtiquetas > 1 ? '20px' : '0',
      }}
    >
      {layout.logo && isEnabled(layout.logo.ativo, true) && (
        <div
          className="etiqueta-element etiqueta-logo"
          style={{
            ...(logoBox ?? getBox(layout.logo)),
            ...getRotationStyle(layout.logo),
          }}
        >
          <img
            src={logoSrc}
            alt="Logo"
            className="etiqueta-logo-img"
            style={logoImageStyle}
            crossOrigin="anonymous"
          />
        </div>
      )}
      {renderCodigoMaterial()}
      {renderDescricao()}
      {renderInfoLine(
        layout.quantidade_info,
        'Quantity:',
        `${dados.quantidade || 0} ${dados.unidade || ''}`.trim(),
      )}
      {renderInfoLine(layout.batch_info, 'Batch:', dados.lote || '---')}
      {renderInfoLine(
        layout.pallet_info,
        'Pallet no.:',
        String(numeroPallet),
      )}
      {renderBarcodeVertical(numeroPallet)}
      {renderBarcodeHorizontal(numeroPallet)}
    </div>
  );


  return (
    <div className="etiqueta-preview-wrapper">
      <div className="etiqueta-preview-grid">
        <Card className="etiqueta-preview-card">
          <Card.Header className="bg-secondary text-white d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-bold mb-1">Etiquetas Preview</div>
              <div className="small text-white-50">
                {quantidadeEtiquetas} etiqueta{quantidadeEtiquetas > 1 ? 's' : ''} · Pallets{' '}
                {numeroPalletInicial} - {numeroPalletInicial + quantidadeEtiquetas - 1}
              </div>
            </div>
            <Button
              size="sm"
              variant="light"
              onClick={() => createPdfPreview(true)}
              title="Baixar PDF das etiquetas"
            >
              Download PDF
            </Button>
          </Card.Header>
          <Card.Body className="etiqueta-preview-body">
            {pdfUrl ? (
              <div className="etiqueta-preview-pdf-wrapper">
                <iframe
                  className="etiqueta-preview-pdf"
                  title="PDF preview"
                  src={`${pdfUrl}#zoom=${pdfZoom}`}
                />
              </div>
            ) : (
              <div className="etiqueta-preview-placeholder">Generating PDF...</div>
            )}
          </Card.Body>
        </Card>
      </div>
      <div
        className="etiqueta-render-source"
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px' }}
      >
        <div ref={labelRef}>
          {Array.from({ length: quantidadeEtiquetas }).map((_, index) =>
            renderSingleEtiqueta(numeroPalletInicial + index, index),
          )}
        </div>
      </div>
    </div>
  );
});
